// SPDX-License-Identifier: BUSL-1.1

/**
 * Agent budget summary card — surfaces monthly spend vs. cap so operators
 * can see at a glance how close an agent is to its budget, and WHY it is
 * paused if `budgetAction='block'` already tripped the kill switch.
 *
 * Data is fed straight from `GET /api/agents/:agentId` (PR #140 increments
 * `budgetSpentThisMonth` atomically per completed job and pauses the agent
 * when it crosses the cap with `budgetAction='block'`).
 *
 * Render rules:
 *   - Skip entirely when `budgetMonthly` is null/undefined ("unlimited").
 *   - Color thresholds: green <70%, yellow 70-90%, red >=90%.
 *   - Paused-by-budget banner appears above the bar when status is `paused`,
 *     `budgetAction='block'`, AND spent >= monthly. Mentions PIO-07's monthly
 *     auto-resume + the manual `budgetSpentThisMonth` reset path.
 *
 * Pattern mirrors `pi-session-panel.tsx` (PR #139): single default export,
 * props interface, internal helpers, ~80 lines.
 *
 * When the optional `agentId` is provided AND the
 * `status-surface-drilldown` feature flag is on (ADR 008 Phase 3A), the
 * card becomes a click target that opens a SurfaceDrilldownSheet with
 * INTENT BINDING framing and the evidence pointer for the
 * `budget_remaining` surface. When the flag is off, behavior is identical
 * to today — non-clickable, no telemetry, same DOM.
 */

import { useState } from 'react';
import type * as React from 'react';
import type { ProjectedSurface } from '@/types/api';
import {
  STATUS_SURFACE_DRILLDOWN_FLAG,
  useAgentSurfaces,
} from '@/hooks/use-agent-surfaces';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { SurfaceDrilldownSheet } from '@/components/status/surface-drilldown-sheet';

/** USD formatter — matches the `$X.XX` convention used in agent-detail.tsx. */
function fmtUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

/** Pick a Tailwind bar color from a 0..100 utilization percentage. */
function barColor(pct: number): string {
  if (pct >= 90) return 'bg-red-500';
  if (pct >= 70) return 'bg-yellow-500';
  return 'bg-green-500';
}

/** Action chip color — block is loud, terminate critical, warn neutral. */
function actionBadgeClass(action: string | undefined): string {
  switch (action) {
    case 'block':
      return 'bg-amber-900/40 text-amber-300';
    case 'terminate':
      return 'bg-red-900/40 text-red-300';
    case 'warn':
    default:
      return 'bg-neutral-800 text-neutral-400';
  }
}

/**
 * Subset of the `/api/agents/:agentId` response that this card reads.
 * Kept loose (`unknown`) where the upstream type is `any` so we can coerce
 * defensively — DB numeric columns come back as strings via the postgres
 * driver, hence the explicit `Number(...)` calls below.
 */
export interface AgentBudgetCardProps {
  agent: {
    budgetMonthly?: number | string | null;
    budgetSpentThisMonth?: number | string | null;
    budgetAction?: string | null;
    status?: string | null;
  };
  /**
   * Optional. When provided AND the `status-surface-drilldown` feature
   * flag is on, the card becomes a click target that opens the surface
   * drilldown sheet (ADR 008 Phase 3A). Without it, behavior is unchanged.
   */
  agentId?: string;
}

/**
 * INTENT BINDING framing for the title attribute. Only used when the
 * drilldown flag is on AND the agent is at or over the watch threshold —
 * the brand vocabulary earns its way into the visible surface by being
 * needed for explanation (per ADR 009 §"Vocabulary discipline rules").
 */
function intentBindingTitle(spent: number, monthly: number, pct: number): string {
  const verb = pct >= 100 ? 'BREACHED' : 'AT RISK';
  return `INTENT BINDING ${verb}: agent at ${fmtUsd(spent)} of ${fmtUsd(monthly)} budget (${pct.toFixed(0)}%). Click for evidence chain.`;
}

/**
 * Render the budget summary card. Returns `null` when `budgetMonthly` is
 * null/undefined so callers can drop this directly into JSX without guards.
 */
export function AgentBudgetCard({
  agent,
  agentId,
}: AgentBudgetCardProps): React.ReactElement | null {
  const drilldownFlag = useFeatureFlag(STATUS_SURFACE_DRILLDOWN_FLAG);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Hook-order rule: always call useAgentSurfaces; the hook itself disables
  // the network fetch when the flag is off or agentId is missing.
  const { data } = useAgentSurfaces(agentId, {
    enabled: drilldownFlag && Boolean(agentId),
  });

  if (agent.budgetMonthly == null) return null;

  const monthly = Number(agent.budgetMonthly);
  const spent =
    agent.budgetSpentThisMonth != null ? Number(agent.budgetSpentThisMonth) : 0;
  const action = agent.budgetAction ?? undefined;
  const status = agent.status ?? undefined;

  const pct = monthly > 0 ? (spent / monthly) * 100 : 0;
  const clampedPct = Math.max(0, Math.min(100, pct));
  const isPausedByBudget =
    status === 'paused' && action === 'block' && spent >= monthly;

  const drilldownEnabled = drilldownFlag && Boolean(agentId);
  const budgetSurface: ProjectedSurface | null =
    data?.surfaces.find((s) => s.type === 'budget_remaining') ?? null;

  // INTENT BINDING framing kicks in at the watch threshold (>=70%) — that's
  // when the brand vocabulary has something to explain. Below that, drilldown
  // is still clickable but the title stays operator-grade.
  const title =
    drilldownEnabled && pct >= 70
      ? intentBindingTitle(spent, monthly, pct)
      : undefined;

  const card = (
    <section
      data-testid="agent-budget-card"
      className="mb-6 rounded-md border border-neutral-800 bg-neutral-900/50 p-4"
      title={title}
    >
      <h3 className="mb-3 text-sm font-medium text-neutral-300">Budget &amp; Cost</h3>

      {isPausedByBudget && (
        <div
          role="alert"
          data-testid="agent-budget-paused-banner"
          className="mb-3 rounded-md border border-red-700 bg-red-950/40 p-3"
        >
          <div className="flex items-start gap-2">
            <span aria-hidden="true" className="text-red-400">
              ⚠
            </span>
            <div className="text-sm">
              <p className="font-medium text-red-300">
                Agent paused — budget exceeded ({fmtUsd(spent)} of{' '}
                {fmtUsd(monthly)} this month)
              </p>
              <p className="mt-1 text-xs text-red-400/80">
                Will auto-resume on the 1st of next month, or reset
                budgetSpentThisMonth manually.
              </p>
            </div>
          </div>
        </div>
      )}

      <p className="mb-2 font-mono text-sm text-neutral-200">
        {fmtUsd(spent)} spent of {fmtUsd(monthly)} budget this month
      </p>

      <div
        role="progressbar"
        aria-valuenow={Math.round(clampedPct)}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-2 w-full overflow-hidden rounded-full bg-neutral-800"
      >
        <div
          className={`h-full rounded-full transition-all ${barColor(clampedPct)}`}
          style={{ width: `${clampedPct}%` }}
        />
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
        <span>Action when exceeded:</span>
        <span
          className={`inline-flex rounded px-2 py-0.5 font-medium ${actionBadgeClass(action)}`}
        >
          {action ?? 'warn'}
        </span>
      </div>
    </section>
  );

  if (!drilldownEnabled) return card;

  return (
    <>
      <button
        type="button"
        className="block w-full cursor-pointer rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        onClick={() => setSheetOpen(true)}
        aria-label="Open budget details"
        data-testid="agent-budget-drilldown-trigger"
      >
        {card}
      </button>
      <SurfaceDrilldownSheet
        agentId={agentId!}
        surface={budgetSurface}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
      />
    </>
  );
}
